/**
 * @fileoverview Full queue section component.
 * @summary Displays all songs in queue (excluding completed/cancelled).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/singer/components/FullQueueSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock, faArrowRotateRight } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../hooks/redux'
import { fetchQueue, type QueueItem } from '../../../store/slices/queueSlice'
import Spinner from '../../../components/Spinner'
import QueueItemRow from './QueueItemRow'
import type { FC } from 'react'

interface FullQueueSectionProps {
  items: QueueItem[]
  isLoading: boolean
  eventId: string
  currentPseudo: string
}

/**
 * @summary Full queue section component.
 * @param {FullQueueSectionProps} props - Component props.
 * @returns {ReactElement} Full queue section component.
 */
const FullQueueSection: FC<FullQueueSectionProps> = ({ items, isLoading, eventId, currentPseudo }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const filteredItems = items.filter((item) => item.status !== 'completed' && item.status !== 'cancelled')

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-(--color-text-primary) flex items-center gap-2">
          <FontAwesomeIcon icon={faClock} />
          {t('singer.fullQueue')}
        </h2>
        <button
          onClick={() => dispatch(fetchQueue(eventId))}
          className="btn btn-secondary btn-sm"
          disabled={isLoading}
          title={t('common.refresh')}
        >
          <FontAwesomeIcon 
            icon={faArrowRotateRight} 
            className={isLoading ? 'animate-spin' : ''}
          />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : filteredItems.length === 0 ? (
        <p className="text-(--color-text-secondary) text-center py-4">
          {t('singer.queueEmpty')}
        </p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredItems.map((item) => (
            <QueueItemRow 
              key={item.id} 
              item={item} 
              currentPseudo={currentPseudo} 
              showSingerPseudo={true}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default FullQueueSection

